import React from 'react';
import { Activity, User, Calendar, Users as UsersIcon, ClipboardList } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { PageLayout, PageHeader, PageCard, pageStyles } from '../PageLayout';
import { formatDateTime as formatDt } from '../../utils/format';

const getIconForAction = (action: string) => {
    if (!action) return <Activity size={18} className="text-gray-500" />;
    if (action.includes('Login')) return <User size={18} className="text-blue-500" />;
    if (action.includes('Kunde')) return <UsersIcon size={18} className="text-green-500" />;
    if (action.includes('Aufgabe')) return <ClipboardList size={18} className="text-orange-500" />;
    if (action.includes('Schicht') || action.includes('Dienst')) return <Calendar size={18} className="text-purple-500" />;
    if (action.includes('Urlaub')) return <Calendar size={18} className="text-teal-500" />;
    return <Activity size={18} className="text-gray-500" />;
};

type LogEntry = { id?: string; action?: string; message?: string; type?: string; createdAt?: string; [k: string]: any };

export default function ActivityLogView({ logs = [] }: { logs?: LogEntry[] }) {
    const { isDark } = useTheme();
    const sorted = [...(logs || [])].sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
    });

    return (
        <PageLayout>
            <PageHeader title="Protokoll" subtitle={sorted.length === 0 ? undefined : `${sorted.length} Einträge`} />
            <PageCard>
            {sorted.length === 0 ? (
                <div className={pageStyles.empty(isDark)}>
                    <p>Keine Protokoll-Einträge vorhanden.</p>
                </div>
            ) : (
            <ul className="space-y-3 max-h-[70vh] overflow-y-auto p-4">
                {sorted.map((entry: LogEntry) => (
                    <li
                        key={entry.id || Math.random()}
                        className={`flex items-start gap-3 p-3 rounded-xl ${isDark ? 'bg-white/10' : 'bg-slate-50'} border ${isDark ? 'border-white/10' : 'border-slate-100'}`}
                    >
                        <div className="flex-shrink-0 mt-0.5">{getIconForAction(entry.action || '')}</div>
                        <div className="min-w-0 flex-1">
                            <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                                {entry.action || 'Aktion'}
                            </div>
                            {entry.message && (
                                <div className={`text-sm mt-0.5 ${isDark ? 'text-white/80' : 'text-slate-600'}`}>
                                    {entry.message}
                                </div>
                            )}
                            {entry.metadata && (entry.metadata.before != null || entry.metadata.after != null) && (
                                <div className={`text-xs mt-2 p-2 rounded-lg ${isDark ? 'bg-white/5 text-white/70' : 'bg-slate-100 text-slate-600'}`}>
                                    {entry.metadata.before != null && <div><span className="font-semibold">Vorher:</span> {typeof entry.metadata.before === 'object' ? JSON.stringify(entry.metadata.before) : String(entry.metadata.before)}</div>}
                                    {entry.metadata.after != null && <div className="mt-1"><span className="font-semibold">Nachher:</span> {typeof entry.metadata.after === 'object' ? JSON.stringify(entry.metadata.after) : String(entry.metadata.after)}</div>}
                                </div>
                            )}
                            <div className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-slate-400'}`}>
                                {formatDt(entry.createdAt)}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
            )}
            </PageCard>
        </PageLayout>
    );
}
